/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  const mobileViewport = window.matchMedia("(max-width: 740px)");
  const pendingColorVariantForms = new WeakSet();
  let protectedScrollPosition = null;
  let protectedScrollFrame = null;

  function getMainProductSection(element) {
    return element.closest(".shopify-section--main-product");
  }

  function isBundleVariantPicker(variantPicker) {
    return variantPicker && variantPicker.classList.contains("g2g-bundle-builder__native-variants");
  }

  function isBundleProductForm(form) {
    const formId = form ? form.getAttribute("id") : "";

    return !!formId && !!document.querySelector(`[data-g2g-bundle-builder][data-form-id="${CSS.escape(formId)}"]`);
  }

  function getRootPixelVariable(name) {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name);
    const parsedValue = Number.parseFloat(value);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }

  function getStickyTopOffset() {
    const cssOffset = getRootPixelVariable("--header-height") * getRootPixelVariable("--enable-sticky-header")
      + getRootPixelVariable("--announcement-bar-height") * getRootPixelVariable("--enable-sticky-announcement-bar");
    const stickyElements = Array.from(document.querySelectorAll(".shopify-section--announcement-bar, .shopify-section--header"))
      .map((element) => {
        return {
          element,
          rect: element.getBoundingClientRect()
        };
      })
      .sort((first, second) => first.rect.top - second.rect.top);
    const measuredOffset = stickyElements.reduce((offset, item) => {
      const styles = window.getComputedStyle(item.element);

      if (styles.position !== "sticky" && styles.position !== "fixed") {
        return offset;
      }

      if (item.rect.height <= 0 || item.rect.bottom <= 0 || item.rect.top > offset + 2) {
        return offset;
      }

      return offset + item.rect.height;
    }, 0);

    return Math.max(cssOffset, measuredOffset) + 12;
  }

  function isColorVariantControl(input) {
    if (!input.matches('product-variants input[name^="option"], product-variants select[name^="option"]')) {
      return false;
    }

    const variantPicker = input.closest("product-variants");
    const section = getMainProductSection(input);

    if (!variantPicker || isBundleVariantPicker(variantPicker) || !section || input.closest("quick-buy-drawer, quick-buy-popover")) {
      return false;
    }

    const optionSelector = input.closest("[data-selector-type]");
    const selectorType = optionSelector ? optionSelector.getAttribute("data-selector-type") : "";
    const optionName = optionSelector
      ? optionSelector.querySelector(".product-form__option-name")?.textContent.trim().replace(/:$/, "").toLowerCase()
      : "";

    return selectorType === "color" || selectorType === "variant-image" || ["color", "colour", "\u989c\u8272", "\u8272\u53f7"].includes(optionName);
  }

  function getColorVariantControl(target) {
    const element = target instanceof Element ? target : null;

    if (!element) {
      return null;
    }

    const directControl = element.closest('product-variants input[name^="option"], product-variants select[name^="option"]');

    if (directControl && isColorVariantControl(directControl)) {
      return directControl;
    }

    const label = element.closest("product-variants label");
    const labeledControl = label && label.htmlFor ? document.getElementById(label.htmlFor) : null;

    return labeledControl && isColorVariantControl(labeledControl) ? labeledControl : null;
  }

  function stopScrollProtection() {
    protectedScrollPosition = null;

    if (protectedScrollFrame) {
      window.cancelAnimationFrame(protectedScrollFrame);
      protectedScrollFrame = null;
    }
  }

  function keepProtectedScrollPosition() {
    if (!protectedScrollPosition) {
      protectedScrollFrame = null;
      return;
    }

    if (Math.abs(window.scrollY - protectedScrollPosition.y) > 1 || Math.abs(window.scrollX - protectedScrollPosition.x) > 1) {
      window.scrollTo(protectedScrollPosition.x, protectedScrollPosition.y);
    }

    if (performance.now() < protectedScrollPosition.until) {
      protectedScrollFrame = window.requestAnimationFrame(keepProtectedScrollPosition);
    } else {
      stopScrollProtection();
    }
  }

  function startScrollProtection() {
    if (!mobileViewport.matches) {
      return;
    }

    if (protectedScrollPosition) {
      protectedScrollPosition.until = performance.now() + 900;

      if (!protectedScrollFrame) {
        protectedScrollFrame = window.requestAnimationFrame(keepProtectedScrollPosition);
      }

      return;
    }

    protectedScrollPosition = {
      x: window.scrollX,
      y: window.scrollY,
      until: performance.now() + 900
    };

    if (!protectedScrollFrame) {
      protectedScrollFrame = window.requestAnimationFrame(keepProtectedScrollPosition);
    }
  }

  function scrollToMainProductMedia(form) {
    if (!mobileViewport.matches) {
      return;
    }

    const section = getMainProductSection(form);
    if (section?.querySelector('[data-scroll-to-media-on-variant-change="false"]')) {
      return;
    }

    const productMedia = section ? section.querySelector("product-media") : null;
    const target = productMedia
      ? productMedia.querySelector(".product__media-item.is-selected .product__media-image-wrapper, .product__media-item.is-selected, .product__media-list-wrapper")
      : null;

    if (!target) {
      return;
    }

    const targetTop = target.getBoundingClientRect().top + window.scrollY - getStickyTopOffset();

    if (window.scrollY <= targetTop + 8) {
      return;
    }

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: "smooth"
    });
  }

  document.addEventListener("pointerdown", (event) => {
    if (getColorVariantControl(event.target)) {
      startScrollProtection();
    }
  }, true);

  document.addEventListener("touchstart", (event) => {
    if (getColorVariantControl(event.target)) {
      startScrollProtection();
    }
  }, { capture: true, passive: true });

  document.addEventListener("change", (event) => {
    const input = event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement ? event.target : null;

    if (input && isColorVariantControl(input) && input.form) {
      const form = input.form;

      startScrollProtection();
      pendingColorVariantForms.add(form);
      setTimeout(() => pendingColorVariantForms.delete(form), 1500);
    }
  }, true);

  document.addEventListener("variant:changed", (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;

    if (!form || isBundleProductForm(form) || !pendingColorVariantForms.has(form)) {
      return;
    }

    pendingColorVariantForms.delete(form);

    requestAnimationFrame(() => {
      setTimeout(() => {
        stopScrollProtection();
        scrollToMainProductMedia(form);
      }, 150);
    });
  });
})();

(() => {
  function getNativeVariantPicker(builder) {
    const formId = builder.getAttribute("data-form-id");

    if (!formId) {
      return null;
    }

    return document.querySelector(`product-variants[form-id="${CSS.escape(formId)}"]`);
  }

  function findNativeOptionControl(variantPicker, position, value) {
    if (!variantPicker || !position) {
      return null;
    }

    const escapedPosition = CSS.escape(String(position));
    const escapedValue = CSS.escape(String(value));

    return variantPicker.querySelector(`input[name="option${escapedPosition}"][value="${escapedValue}"]`)
      || variantPicker.querySelector(`select[name="option${escapedPosition}"]`);
  }

  function selectNativeBundleOption(builder, button) {
    const variantPicker = getNativeVariantPicker(builder);

    if (!variantPicker || !button) {
      return false;
    }

    const position = button.getAttribute("data-g2g-bundle-option-position");
    const value = button.getAttribute("data-g2g-bundle-option-value");
    const nativeControl = findNativeOptionControl(variantPicker, position, value);

    if (!nativeControl) {
      return false;
    }

    if (nativeControl.tagName === "SELECT") {
      if (nativeControl.value === value) {
        return false;
      }

      nativeControl.value = value;
    } else {
      if (nativeControl.checked) {
        return false;
      }

      nativeControl.checked = true;
    }

    nativeControl.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  const bundleImageRequests = new WeakMap();

  function updateBundleItemImage(image, src, srcset) {
    const media = image.closest(".g2g-bundle-builder__media");
    const previous = bundleImageRequests.get(image);
    const nextSrcset = srcset || "";

    if (src && previous?.src === src && previous.srcset === nextSrcset) {
      return;
    }

    previous?.cancel();
    bundleImageRequests.delete(image);

    if (!src || (image.getAttribute("src") === src
      && (image.getAttribute("srcset") || "") === nextSrcset
      && image.complete && image.naturalWidth > 0)) {
      media?.removeAttribute("aria-busy");
      return;
    }

    const preload = new Image();
    const request = { src, srcset: nextSrcset, cancel: () => {} };
    const finish = (success) => {
      if (bundleImageRequests.get(image) !== request) {
        return;
      }

      request.cancel();
      if (success) {
        if (nextSrcset) {
          image.setAttribute("srcset", nextSrcset);
        } else {
          image.removeAttribute("srcset");
        }
        image.setAttribute("src", src);
      }
      bundleImageRequests.delete(image);
      media?.removeAttribute("aria-busy");
    };
    const timeout = setTimeout(() => finish(false), 20000);

    request.cancel = () => {
      clearTimeout(timeout);
      preload.onload = null;
      preload.onerror = null;
    };
    bundleImageRequests.set(image, request);
    media?.setAttribute("aria-busy", "true");
    preload.onload = async () => {
      try {
        await preload.decode();
      } catch {
        // A loaded image can still be displayed if decoding is unsupported.
      }
      finish(true);
    };
    preload.onerror = () => finish(false);
    preload.sizes = image.sizes;
    preload.srcset = nextSrcset;
    preload.src = src;
  }

  function syncBuilderState(builder, options = {}) {
    const variantPicker = getNativeVariantPicker(builder);
    const allowAutoSelect = options.allowAutoSelect !== false;

    if (!variantPicker) {
      return;
    }

    builder.querySelectorAll("[data-g2g-bundle-option-position]").forEach((button) => {
      const position = button.getAttribute("data-g2g-bundle-option-position");
      const value = button.getAttribute("data-g2g-bundle-option-value");
      const nativeControl = findNativeOptionControl(variantPicker, position, value);
      let isActive = false;

      if (nativeControl) {
        isActive = nativeControl.tagName === "SELECT"
          ? nativeControl.value === value
          : nativeControl.checked;
      }

      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");

      if (isActive) {
        const labelTargetId = button.getAttribute("data-g2g-bundle-option-label-target");
        const labelTarget = labelTargetId ? builder.querySelector(`#${CSS.escape(labelTargetId)}`) : null;

        if (labelTarget) {
          labelTarget.textContent = value;
        }

        const itemImageSrc = button.getAttribute("data-g2g-bundle-item-image-src");
        const itemImageSrcset = button.getAttribute("data-g2g-bundle-item-image-srcset");
        const itemUrl = button.getAttribute("data-g2g-bundle-item-url");
        const itemCard = button.closest("[data-g2g-bundle-item]");
        const itemImage = itemCard ? itemCard.querySelector("[data-g2g-bundle-item-image]") : null;

        if (itemCard && itemUrl) {
          itemCard.querySelectorAll("[data-g2g-bundle-item-link]").forEach((link) => {
            link.setAttribute("href", itemUrl);
          });
        }

        if (itemImage) {
          updateBundleItemImage(itemImage, itemImageSrc, itemImageSrcset);
        }
      }
    });

    if (!allowAutoSelect) {
      return;
    }

    builder.querySelectorAll("[data-g2g-bundle-option]").forEach((optionGroup) => {
      const activeButton = optionGroup.querySelector("[data-g2g-bundle-option-position].is-active");
      const firstButton = optionGroup.querySelector("[data-g2g-bundle-option-position]");

      if (!activeButton && firstButton && selectNativeBundleOption(builder, firstButton)) {
        syncBuilderState(builder, { allowAutoSelect: false });
      }
    });
  }

  function initBundleBuilder(builder) {
    if (builder.hasAttribute("data-g2g-bundle-builder-ready")) {
      return;
    }

    builder.setAttribute("data-g2g-bundle-builder-ready", "true");

    builder.addEventListener("click", (event) => {
      const button = event.target instanceof Element
        ? event.target.closest("[data-g2g-bundle-option-position]")
        : null;

      if (!button || !builder.contains(button)) {
        return;
      }

      const variantPicker = getNativeVariantPicker(builder);
      if (!variantPicker) {
        return;
      }

      if (selectNativeBundleOption(builder, button)) {
        syncBuilderState(builder);
      }
    });

    const form = document.getElementById(builder.getAttribute("data-form-id"));

    if (form) {
      form.addEventListener("variant:changed", () => syncBuilderState(builder));
    }

    syncBuilderState(builder);
  }

  function initBundleBuilders(scope = document) {
    scope.querySelectorAll("[data-g2g-bundle-builder]").forEach(initBundleBuilder);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initBundleBuilders());
  } else {
    initBundleBuilders();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initBundleBuilders(event.target);
  });

  document.addEventListener("variant:changed", (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;

    if (!form) {
      return;
    }

    const quickBuy = form.closest("quick-buy-drawer, quick-buy-popover");
    const imageDataElement = quickBuy ? quickBuy.querySelector("[data-g2g-bundle-quick-buy-static-image]") : null;
    const imageElement = quickBuy ? quickBuy.querySelector(".quick-buy-product__image") : null;

    if (!imageDataElement || !imageElement) {
      return;
    }

    requestAnimationFrame(() => {
      try {
        const imageData = JSON.parse(imageDataElement.textContent);

        if (imageData.alt) {
          imageElement.setAttribute("alt", imageData.alt);
        }

        if (imageData.width) {
          imageElement.setAttribute("width", imageData.width);
        }

        if (imageData.height) {
          imageElement.setAttribute("height", imageData.height);
        }

        if (imageData.src) {
          imageElement.setAttribute("src", imageData.src);
        }

        if (imageData.srcset) {
          imageElement.setAttribute("srcset", imageData.srcset);
        }
      } catch (error) {
        // Keep the theme's default quick-buy image behavior if the JSON cannot be parsed.
      }
    });
  });

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches("[data-g2g-bundle-builder]")) {
          initBundleBuilder(node);
        } else {
          initBundleBuilders(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

(() => {
  function loadDeferredCountryFlags(container) {
    container.querySelectorAll("img.localization-country-flag--deferred[data-src]").forEach((image) => {
      image.src = image.dataset.src;
      image.removeAttribute("data-src");
      image.classList.remove("localization-country-flag--deferred");
    });
  }

  document.addEventListener("openable-element:open", (event) => {
    const popover = event.target;

    if (!(popover instanceof Element) || popover.tagName !== "POPOVER-CONTENT") {
      return;
    }

    if (!popover.querySelector("img.localization-country-flag--deferred")) {
      return;
    }

    loadDeferredCountryFlags(popover);
  });
})();
